import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function ProductsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '' });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await api.getProducts();
      setProducts(res.data);
    } catch { toast.error('Erro ao carregar produtos'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, price: parseFloat(form.price) };
      if (editing) {
        await api.updateProduct(editing.id, data);
        toast.success('Produto atualizado!');
      } else {
        await api.createProduct(data);
        toast.success('Produto criado!');
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ name: '', description: '', price: '' });
      fetchProducts();
    } catch { toast.error('Erro ao salvar produto'); }
  };

  const handleEdit = (product) => {
    setEditing(product);
    setForm({ name: product.name, description: product.description || '', price: String(product.price) });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este produto?')) return;
    try {
      await api.deleteProduct(id);
      toast.success('Produto excluido!');
      fetchProducts();
    } catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="products-title">
                Produtos / Kits
              </h1>
              <p className="text-slate-600">Catalogo de produtos e kits de venda</p>
            </div>
            {isAdmin && (
              <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditing(null); setForm({ name: '', description: '', price: '' }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-button">
                    <Plus className="w-4 h-4 mr-2" /> Novo Produto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editing ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Nome do Produto / Kit</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ex: Kit 3 Serum" data-testid="product-name-input" />
                    </div>
                    <div>
                      <Label>Descricao</Label>
                      <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descricao do produto" data-testid="product-description-input" />
                    </div>
                    <div>
                      <Label>Valor (R$)</Label>
                      <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required placeholder="0.00" data-testid="product-price-input" />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-product-button">
                      {editing ? 'Salvar' : 'Criar Produto'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {products.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Nenhum produto cadastrado</p>
              {isAdmin && <p className="text-sm text-slate-400 mt-1">Clique em "Novo Produto" para comecar</p>}
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Descricao</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead>Acoes</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-slate-500">{p.description || '-'}</TableCell>
                      <TableCell className="text-emerald-600 font-semibold">
                        R$ {(p.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        <Badge className={p.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {p.active !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-product-${p.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
